import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Snackbar,
  CircularProgress, Tooltip, IconButton
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import staffService from '../services/staffService';

const StaffManagementTab = () => {
  const [staff, setStaff] = useState([]);
  const [maxStaffCount, setMaxStaffCount] = useState(5);
  const [currentCount, setCurrentCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // 新增員工 dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ username: '', email: '', name: '' });
  const [addLoading, setAddLoading] = useState(false);

  // 確認 dialog（重設密碼 / 移除）
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'reset' | 'remove', user }
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 通知
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      const res = await staffService.listStaff();
      if (res.success) {
        setStaff(res.data.staff);
        setMaxStaffCount(res.data.maxStaffCount);
        setCurrentCount(res.data.currentCount);
      }
    } catch (err) {
      setNotification({ open: true, message: '取得員工列表失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // 新增員工
  const handleAdd = async () => {
    if (!addForm.username || !addForm.email) return;
    try {
      setAddLoading(true);
      const res = await staffService.addStaff(addForm);
      if (res.success) {
        setNotification({ open: true, message: res.message, severity: 'success' });
        setAddOpen(false);
        setAddForm({ username: '', email: '', name: '' });
        fetchStaff();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '新增員工失敗';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setAddLoading(false);
    }
  };

  // 確認動作
  const handleConfirm = async () => {
    if (!confirmAction) return;
    try {
      setConfirmLoading(true);
      if (confirmAction.type === 'reset') {
        const res = await staffService.resetPassword(confirmAction.user.id);
        setNotification({ open: true, message: res.message, severity: 'success' });
      } else if (confirmAction.type === 'remove') {
        const res = await staffService.removeStaff(confirmAction.user.id);
        setNotification({ open: true, message: res.message, severity: 'success' });
        fetchStaff();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '操作失敗';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmAction(null);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin' || role === 'super_admin') {
      return <Chip label="管理員" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />;
    }
    return <Chip label="員工" size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }} />;
  };

  const getInitial = (name) => (name || '?')[0].toUpperCase();

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#7b1fa2', '#2e7d32', '#e65100', '#c62828', '#00838f'];
    const idx = (name || '').charCodeAt(0) % colors.length;
    return colors[idx];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>員工管理</Typography>
          <Typography variant="body2" color="text.secondary">
            管理公司內部員工帳號 — {currentCount} / {maxStaffCount} 位員工
          </Typography>
        </Box>
        <Tooltip title={currentCount >= maxStaffCount ? `已達上限 (${maxStaffCount} 人)` : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddOpen(true)}
              disabled={currentCount >= maxStaffCount}
            >
              新增員工
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Staff Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>姓名</TableCell>
              <TableCell>信箱</TableCell>
              <TableCell>角色</TableCell>
              <TableCell>最後登入</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((user) => {
              const isOwner = user.role === 'admin' || user.role === 'super_admin';
              const hasLoggedIn = !!user.lastLogin;
              return (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: getAvatarColor(user.username), fontSize: 14, fontWeight: 700 }}>
                        {getInitial(user.username)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{user.username}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {hasLoggedIn
                        ? new Date(user.lastLogin).toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : '尚未登入'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hasLoggedIn ? '#4caf50' : '#ff9800' }} />
                      <Typography variant="body2">{hasLoggedIn ? '啟用' : '待啟用'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {isOwner ? (
                      <Typography variant="body2" color="text.disabled">（擁有者）</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="重設密碼">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => { setConfirmAction({ type: 'reset', user }); setConfirmOpen(true); }}
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="移除">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setConfirmAction({ type: 'remove', user }); setConfirmOpen(true); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        新增員工後，系統會自動產生臨時密碼並寄送至員工信箱。員工使用臨時密碼即可登入系統。
      </Alert>

      {/* Add Staff Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增員工</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="使用者名稱"
              value={addForm.username}
              onChange={(e) => setAddForm(prev => ({ ...prev, username: e.target.value }))}
              required
              fullWidth
              placeholder="例：sarah_chen"
            />
            <TextField
              label="電子信箱"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
              placeholder="例：sarah@company.com"
            />
            <TextField
              label="姓名（選填）"
              value={addForm.name}
              onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              placeholder="例：Sarah Chen"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addLoading}>取消</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={addLoading || !addForm.username || !addForm.email}
          >
            {addLoading ? <CircularProgress size={20} /> : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog (Reset Password / Remove) */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAction?.type === 'reset' ? '重設密碼' : '移除員工'}
        </DialogTitle>
        <DialogContent>
          {confirmAction?.type === 'reset' ? (
            <Typography>
              確定要重設 <strong>{confirmAction.user.username}</strong> 的密碼？
              新密碼將寄送至 {confirmAction.user.email}
            </Typography>
          ) : (
            <Typography color="error">
              確定要移除 <strong>{confirmAction?.user?.username}</strong>？此操作無法復原。
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={confirmLoading}>取消</Button>
          <Button
            variant="contained"
            color={confirmAction?.type === 'remove' ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? <CircularProgress size={20} /> : '確定'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} onClose={() => setNotification(prev => ({ ...prev, open: false }))}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffManagementTab;
